/* eslint-disable react/style-prop-object */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { CircularProgress, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { HTMLAttributes, Key, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { ProjectDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useQueryListProjects } from '../../../../core/queries/projects.queries';
import { ProjectPathParams, Paths } from '../../../../app/router/app.router';
import { Autocomplete } from '@mui/material';

// TODO: Fix global pages and add this back in
//type CurrentStateGlobal = { type: 'global' }
export type BuildingSelectProject = {
  type: 'project';
  projectID: string;
  shortcode?: string;
  name: string;
  level: number;
  projectType?: string;
};
type CurrentState = { type: 'project'; projectID: string; shortcode?: string }; // | CurrentStateGlobal

// Siblings are ordered by name, then any project with an explicit searchPriority is pushed below
// the ones that leave it unset (which are treated as the default priority of 1).
const DEFAULT_SEARCH_PRIORITY = 1;

function compareSiblings(a: ProjectDto, b: ProjectDto): number {
  const priorityDiff = (a.searchPriority ?? DEFAULT_SEARCH_PRIORITY) - (b.searchPriority ?? DEFAULT_SEARCH_PRIORITY);
  return priorityDiff !== 0 ? priorityDiff : a.name.localeCompare(b.name);
}

export function sortProjectsByHierarchy(projects: ProjectDto[]): BuildingSelectProject[] {
  const projectsById = new Map<string, ProjectDto>();
  const projectsChildren = new Map<string, string[]>();
  const rootProjects: ProjectDto[] = [];

  for (const project of projects) {
    projectsById.set(project.id, project);
    // Check that the parent project is present, otherwise will become root.
    if (project.parentID && project.parentID !== project.id && projects.some((p) => p.id === project.parentID)) {
      const children = projectsChildren.get(project.parentID) || [];
      children.push(project.id);
      projectsChildren.set(project.parentID, children);
    } else {
      rootProjects.push(project);
    }
  }

  const result: BuildingSelectProject[] = [];
  rootProjects.sort(compareSiblings);
  const searchStack = rootProjects.map((p) => ({ ...p, level: 0 }));
  searchStack.reverse();
  while (searchStack.length > 0) {
    const curr = searchStack.pop();
    if (curr === undefined) {
      break;
    }
    result.push({
      type: 'project',
      name: curr.name,
      projectID: curr.id,
      shortcode: curr.shortcode,
      level: curr.level,
      projectType: curr.projectType
    });
    const children = (projectsChildren.get(curr.id) || [])
      .map((id) => projectsById.get(id))
      .filter((p): p is ProjectDto => p !== undefined)
      .map((p) => ({ ...p, level: curr.level + 1 }))
      .sort(compareSiblings)
      .reverse();
    searchStack.push(...children);
  }

  return result;
}

export function BuildingSelect({ params }: { params: ProjectPathParams<Paths.project> }) {
  const projectsResult = useQueryListProjects();
  /*const hasGlobalPermissions = useHasPermissions({
    gfr: `*`,
    action: '*'
  });*/
  const location = useLocation();
  const history = useHistory();

  const handleChange = useCallback(
    (state: CurrentState) => {
      // early exit if projectId hasn't changed
      if (
        params.projectID === state.projectID ||
        (typeof params.shortcode !== 'undefined' && params.shortcode === state.shortcode)
      ) {
        return;
      }

      //if (state.type === 'project') {
      let newPath = `/projects/${state.shortcode || state.projectID}`;

      // Try to keep current page if already in a project
      if (params.projectID !== undefined) {
        const parts = location.pathname.split('/');
        const projectSubPath = parts[3] || '';

        if (projectSubPath.length) {
          newPath += `/${projectSubPath}`;
        }
      }

      const path = {
        ...location,
        pathname: newPath,
        state: {
          projectSwitch: true
        }
      };

      history.push(path);
      /*} else {
      history.push('/global')
    }*/
    },
    [history, location, params.projectID, params.shortcode]
  );

  const projectOptions: BuildingSelectProject[] | undefined = useMemo(
    () => (projectsResult.data ? sortProjectsByHierarchy(projectsResult.data) : []),
    [projectsResult.data]
  );

  return (
    (projectOptions && (
      <BuildingSelectDropdown
        onStateChange={handleChange}
        /*allowSelectGlobal={hasGlobalPermissions}*/
        currentState={{ type: 'project', ...params }}
        projects={projectOptions}
        isLoading={projectsResult.isLoading}
      />
    )) ||
    null
  );
}

const useStyles = makeStyles(() => ({
  textfield: {
    background: '#FFFFFF',
    color: '#131313',
    borderRadius: '4px',
    textAlign: 'center',
    marginTop: 'auto',
    marginBottom: 'auto'
  },
  globalMenuItem: {
    // borderBottom: '1px solid #ccc',
  },
  optionName: {
    // The hierarchy prefix is built from box drawing characters, so keep its spacing intact
    whiteSpace: 'pre',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));

// type ProjectItem = {
//   type: 'project',
//   id: string;
//   name: string;
// }

// type GlobalItem = {
//   type: 'global'
// }

// type DropdownItemType = ProjectItem | GlobalItem

export interface BuildingSelectDropdownProps {
  currentState: CurrentState;
  // TODO: Add global item back in when the global page is built
  //allowSelectGlobal: boolean;
  projects: BuildingSelectProject[];
  onStateChange: (state: CurrentState) => void;
  isLoading: boolean;
}

export function BuildingSelectDropdown({
  currentState,
  projects,
  isLoading,
  /*allowSelectGlobal,*/ onStateChange
}: BuildingSelectDropdownProps) {
  const items = useMemo(
    () => projects.map((p) => ({ project: p, isSelected: p.projectID === currentState.projectID })),
    [projects, currentState.projectID]
  );

  const classes = useStyles();

  const renderProjectName = useCallback((project: BuildingSelectProject) => {
    const prefix =
      project.level < 1
        ? ''
        : `${String.fromCharCode(9474)} `.repeat(project.level - 1) +
          `${String.fromCharCode(9500) + String.fromCharCode(9472) + String.fromCharCode(160)}`;
    return `${prefix}${project.name}`;
  }, []);

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement> & { key: Key }, project: BuildingSelectProject) => (
      <li {...props}>
        <span className={classes.optionName}>{renderProjectName(project)}</span>
      </li>
    ),
    [renderProjectName, classes.optionName]
  );

  const findProjectById = useCallback((id: string) => projects.find((p) => p.projectID === id), [projects]);

  const handleSelectMenu = (dropdownItem: CurrentState) => {
    onStateChange(dropdownItem);
  };

  // If no options, don't show dropdown
  if (items.length === 0) return null;

  return (
    <>
      <div className={classes.textfield + ' topbar-item'}>
        <Autocomplete
          id="buildingSelectMenu"
          blurOnSelect
          disablePortal
          noOptionsText="No match"
          options={items.map(({ project: dropdownItem }) => dropdownItem)}
          getOptionLabel={(option) => renderProjectName(option)}
          renderOption={renderOption}
          onChange={(_event, value) => {
            if (value) handleSelectMenu(value);
          }}
          value={items.find((item) => item.isSelected)?.project}
          style={{ width: 270 }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label={
                  isLoading ? (
                    <>
                      <CircularProgress style={{ width: '1em', height: '1em' }} />
                    </>
                  ) : findProjectById(currentState.projectID) ? null : (
                    <>Select a project</>
                  )
                }
                variant="standard"
                slotProps={{
                  ...params.slotProps,
                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    value: (params.slotProps.htmlInput as any).value.replace(/^[^\w]+/g, '')
                  }
                }}
              />
            );
          }}
        />
      </div>
    </>
  );
}
